import { Fragment, ReactElement } from 'react';
import { queries } from '@zoopla-eng/zds-components/themes/queries';
import {
    CmsArticleCollection,
    CmsArticleCollectionProps,
} from '@zoopla/cms/article-collection';
import { Banner } from '@zoopla/banner';

import {
    ArticleCollection as ArticleCollectionGQL,
    CampaignCta as CampaignCTAGQL,
    Banner as BannerGQL,
    ContentBlock,
} from '../graphql.generated';
import {
    CampaignCta,
    HomeValuationForm,
} from '@zoopla/landing-pages/campaign-cta';
import { Document } from '@contentful/rich-text-types';
import { Box } from '@zoopla-eng/zds-components/box';
import { CONTENTFUL_RICH_TEXT_OPTIONS } from '@zoopla/cms/contentful-render-options';
import {
    ValuePropositionCollection,
    ValuePropositionCollectionProps,
} from '@zoopla/value-propositions';
import ReactComponent from './components/ReactComponent';
import { CmsTwoColumnSplit } from '@zoopla/cms/two-column-split';
import { CmsLogoCollection } from '@zoopla/cms/logo-collection';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import SmallPrint from './components/SmallPrint/SmallPrint';
import expandedFaqs from './components/ExpandedFaqCollection/ExpandedFaqCollection.helper';
import { setupLogger } from '@zoopla/logger';
import { CmsSeoLinks, seoLinkCheck } from '@zoopla/cms/seo-links';
import type { SeoLink } from '@zoopla/cms/seo-links';

const logger = setupLogger({
    custodian: 'platform',
    component: 'component-mapper',
});

type LandingPageBanner = Omit<BannerGQL, 'description'> & {
    bannerDescription?: Document;
};

const articleCollection = (props: ArticleCollectionGQL) => (
    // TODO: Temporary fix as part of the Vercel migration work
    // Re-generating this libs graphql schema will cause a lint error
    // proper fix to be implemented post-migration
    <CmsArticleCollection
        articleCollectionData={{
            ...props,
            description: props.description,
            contentType: props.contentType as string,
        }}
        carouselConfig={{
            breakpoints: [
                {
                    size: queries.M[100],
                    numCards: 3,
                },
            ],
        }}
        seeMoreLink={
            props.seeMoreLink as CmsArticleCollectionProps['seeMoreLink']
        }
        horizontalCardLayout={false}
    />
);

const campaignCta = (props: CampaignCTAGQL) => (
    // TODO: Temporary fix as part of the Vercel migration work
    // Re-generating this libs graphql schema will cause a lint error
    // proper fix to be implemented post-migration
    <CampaignCta
        title={props.title || ''}
        body={props.description || ''}
        image={props.image?.url || ''}
    >
        <HomeValuationForm eventCategory="homepage" />
    </CampaignCta>
);

const componentBanner = (banner: LandingPageBanner) => (
    <Banner
        title={banner.title}
        body={banner.bannerDescription}
        imageSrc={banner.imageSrc}
        bgColor={banner.backgroundColour}
        buttons={
            banner.buttons
                ? banner.buttons.map((button) => ({
                      label: button?.buttonLabel || '',
                      url: button?.buttonUrl || '',
                      category: button?.category || 'primary',
                      eventTracking: button?.eventTracking ?? undefined,
                  }))
                : undefined
        }
    />
);

const valuePropositionCollection = (
    valueProp: ValuePropositionCollectionProps
) => <ValuePropositionCollection {...valueProp} />;

function componentMap(contentBlock: ContentBlock, slug: string) {
    switch (contentBlock.__typename) {
        case 'ArticleCollection':
            return articleCollection({
                ...contentBlock,
                // TODO: Properly fix these typescript definitions
                // Part of the Vercel/ZDS swarm work, just a quick fix for now
                description: contentBlock.description,
            });
        case 'Banner':
            return componentBanner({
                ...contentBlock,
            });
        case 'CampaignCta':
            return campaignCta(contentBlock);
        case 'FaqCollection':
            return expandedFaqs(contentBlock);
        case 'SeoLinksContainer': {
            const links: SeoLink[] = contentBlock.seoLinks.map(seoLinkCheck);
            return (
                <CmsSeoLinks
                    title={contentBlock.title}
                    seoLinks={links}
                    seeMoreLinkHref={contentBlock?.seeAllLinkSlug ?? undefined}
                    seeMoreLinkText={contentBlock?.seeAllLinkLabel ?? undefined}
                />
            );
        }
        case 'VariationContainer':
            return null;
        case 'ValuePropositionCollection':
            return valuePropositionCollection({ data: contentBlock });
        case 'ReactComponent':
            return (
                <ReactComponent
                    slug={slug}
                    componentName={contentBlock.componentName}
                    title={contentBlock.title}
                    body={contentBlock.body}
                />
            );
        case 'TwoColumnSplit':
            return (
                <CmsTwoColumnSplit
                    leftColumn={contentBlock.leftColumn}
                    rightColumn={contentBlock.rightColumn}
                />
            );
        case 'LogoCollection':
            return (
                <CmsLogoCollection
                    title={contentBlock.heading}
                    logos={contentBlock.logos}
                />
            );
        case 'SmallPrint':
            return (
                <SmallPrint
                    content={contentBlock.smallPrintContent as Document}
                />
            );
        case 'ComponentRichText':
            return (
                <Box display="flex" flexDirection="column" rowGap="S400">
                    {documentToReactComponents(
                        contentBlock.content as Document,
                        CONTENTFUL_RICH_TEXT_OPTIONS
                    )}
                </Box>
            );
    }

    return null;
}

const LandingPagesContentMapper = (
    contentBlock: ContentBlock[],
    slug: string
): ReactElement[] => {
    const components: ReactElement[] = [];

    contentBlock.filter(Boolean).forEach((item, index) => {
        if (!item.__typename) {
            logger.warn(`No content type found for ${JSON.stringify(item)}`);
        } else {
            const element = componentMap(item, slug);
            if (element) {
                components.push(<Fragment key={index}>{element}</Fragment>);
            } else {
                logger.warn(
                    `Content mapping does not yet support ${item.__typename}.`
                );
            }
        }
    });

    return components;
};

export default LandingPagesContentMapper;
